import React, { useState } from "react";
import transformExponentialNumber from "../../../utils/numbersFormatting";
import PageSpinner from "../../elements/spinner/PageSpinner";
import RenderStatus from "../../styles/styledStatus";
import Notification from "../../elements/notification/Notification";
import { TimestampToDateMoment } from "../../../utils/timestampToDate";
import { Alert } from "@material-ui/lab";
import { CopyToClipboard } from "react-copy-to-clipboard/lib/Component";
import { useTranslation } from "react-i18next";

import { StyledCol, StyledTable, StyledTableBody, StyledTableHead, StyledTableRow } from "../../styles/styledTable";
import { StyledPaymentDetailsContent } from "../styledPayment";
import { StyledBlockLabel, StyledFormBlock, StyledFormGrid } from "../../styles/styledTabsForm";
import { StyledConnectionLogo } from "../../elements/connection/styledConnection";
import { StyledBreakWord } from "../../styles/styledBreakWord";
import { StyledCopy } from "../../styles/styledCopy";

const PaymentDetailsContent = ({ payment, transferUserEmail }) => {
  const { t } = useTranslation("transactionDetailsPage");

  const [notification, setNotification] = useState({
    visible: false,
    type: "",
    message: ""
  });

  if (!payment) {
    return <PageSpinner
      position="center"
      pt="20"
      pb="20"
    />;
  }

  const {
    id,
    amount,
    connection,
    createdAt,
    updatedAt,
    currency,
    flowData,
    attributes,
    paymentSystem,
    processedAmount,
    processingId,
    referenceId,
    status,
    paymentType,
    transactionType,
    relatedReferenceId,
    note
  } = payment;

  let parsedFlowData = flowData;

  if(parsedFlowData.length === undefined) {
    parsedFlowData = Object.values(flowData);
  }

  return (
    <>
      <Notification
        notification={notification}
        setNotification={setNotification}
      />
      <StyledPaymentDetailsContent>
        <StyledFormGrid className="payment-details-content">
          <StyledFormBlock className="payment-details-content__block">
            <StyledBlockLabel>
              Main
            </StyledBlockLabel>
            <StyledTable size="14">
              <StyledTableHead col="2" className="payment-details-content__head">
                <StyledCol>{t("key")}:</StyledCol>
                <StyledCol>{t("value")}:</StyledCol>
              </StyledTableHead>
              <StyledTableBody>
                <StyledTableRow col="2" className="payment-details-content__row">
                  <StyledCol data-title={t("key")}>
                    Id
                  </StyledCol>
                  <StyledCol data-title={t("value")}>
                    <CopyToClipboard
                      text={id}
                      onCopy={() => {
                        setNotification({ ...notification, visible: true, type: "success", message: t("copied") });
                      }}
                    >
                      <StyledBreakWord as="p">
                        {id} <StyledCopy title={t("copy")} className="icon-light-copy" />
                      </StyledBreakWord>
                    </CopyToClipboard>
                  </StyledCol>
                </StyledTableRow>
                <StyledTableRow col="2" className="payment-details-content__row">
                  <StyledCol data-title={t("key")}>
                    Reference id
                  </StyledCol>
                  <StyledCol data-title={t("value")}>
                    <CopyToClipboard
                      text={referenceId}
                      onCopy={() => {
                        setNotification({ ...notification, visible: true, type: "success", message:  t("copied") });
                      }}
                    >
                      <StyledBreakWord as="p">
                        {referenceId} <StyledCopy title={t("copy")} className="icon-light-copy" />
                      </StyledBreakWord>
                    </CopyToClipboard>
                  </StyledCol>
                </StyledTableRow>
                <StyledTableRow col="2" className="payment-details-content__row">
                  <StyledCol data-title={t("key")}>
                    Processing id
                  </StyledCol>
                  <StyledCol inline data-title={t("value")}>
                    <p>{processingId ?? "-"}</p>
                  </StyledCol>
                </StyledTableRow>
                <StyledTableRow col="2" className="payment-details-content__row">
                  <StyledCol data-title={t("key")}>
                    {t("status")}
                  </StyledCol>
                  <StyledCol inline data-title={t("value")}>
                    <RenderStatus status={status.value} />
                  </StyledCol>
                </StyledTableRow>
                <StyledTableRow col="2" className="payment-details-content__row">
                  <StyledCol data-title={t("key")}>
                    {t("createdAt")}
                  </StyledCol>
                  <StyledCol data-title={t("value")}>
                    {TimestampToDateMoment(createdAt)}
                  </StyledCol>
                </StyledTableRow>
                <StyledTableRow col="2" className="payment-details-content__row">
                  <StyledCol data-title={t("key")}>
                    {t("updatedAt")}
                  </StyledCol>
                  <StyledCol data-title={t("value")}>
                    {TimestampToDateMoment(updatedAt)}
                  </StyledCol>
                </StyledTableRow>
                <StyledTableRow col="2" className="payment-details-content__row">
                  <StyledCol data-title={t("key")}>
                    {t("currency")}
                  </StyledCol>
                  <StyledCol data-title={t("value")}>
                    <p>{currency.asset}</p>
                  </StyledCol>
                </StyledTableRow>
                <StyledTableRow col="2" className="payment-details-content__row">
                  <StyledCol data-title={t("key")}>
                    {t("paymentSystem")}
                  </StyledCol>
                  <StyledCol data-title={t("value")}>
                    <p>{paymentSystem.name}</p>
                  </StyledCol>
                </StyledTableRow>
                {transferUserEmail &&
                  <StyledTableRow col="2" className="payout-details-content__row">
                    <StyledCol data-title={t("key")}>
                      {t("transferUserEmail.payment")}
                    </StyledCol>
                    <StyledCol inline data-title={t("value")}>
                      <p>{transferUserEmail}</p>
                    </StyledCol>
                  </StyledTableRow>
                }
                <StyledTableRow col="2" className="payment-details-content__row">
                  <StyledCol data-title={t("key")}>
                    {t("amount")}
                  </StyledCol>
                  <StyledCol data-title={t("value")}>
                    <p>{amount}</p>
                  </StyledCol>
                </StyledTableRow>
                <StyledTableRow col="2" className="payment-details-content__row">
                  <StyledCol data-title={t("key")}>
                    {t("processedAmount.payment")}
                  </StyledCol>
                  <StyledCol data-title={t("value")}>
                    <p>{processedAmount}</p>
                  </StyledCol>
                </StyledTableRow>
                <StyledTableRow col="2" className="payment-details-content__row">
                  <StyledCol data-title={t("key")}>
                    {t("paymentType")}
                  </StyledCol>
                  <StyledCol data-title={t("value")}>
                    <p>{paymentType}</p>
                  </StyledCol>
                </StyledTableRow>
                <StyledTableRow col="2" className="payment-details-content__row">
                  <StyledCol data-title={t("key")}>
                    {t("transactionType")}
                  </StyledCol>
                  <StyledCol data-title={t("value")}>
                    <p>{transactionType}</p>
                  </StyledCol>
                </StyledTableRow>
                <StyledTableRow col="2" className="payment-details-content__row">
                  <StyledCol data-title={t("key")}>
                    Related reference id
                  </StyledCol>
                  <StyledCol inline data-title={t("value")}>
                    <p>{relatedReferenceId ?? "-"}</p>
                  </StyledCol>
                </StyledTableRow>
                <StyledTableRow col="2" className="payment-details-content__row">
                  <StyledCol data-title={t("key")}>
                    {t("note")}
                  </StyledCol>
                  <StyledCol data-title={t("value")}>
                    <p>{note ?? '-'}</p>
                  </StyledCol>
                </StyledTableRow>
              </StyledTableBody>
            </StyledTable>
          </StyledFormBlock>

          <StyledFormBlock className="payment-details-content__block">
            <StyledBlockLabel>
              Connection
            </StyledBlockLabel>
            <StyledTable size="14">
              <StyledTableHead col="2" className="payment-details-content__head">
                <StyledCol>{t("key")}:</StyledCol>
                <StyledCol>{t("value")}:</StyledCol>
              </StyledTableHead>
              <StyledTableBody>
                <StyledTableRow col="2" className="payment-details-content__row">
                  <StyledCol data-title={t("key")}>
                    {t("connectionId")}
                  </StyledCol>
                  <StyledCol data-title={t("value")}>
                    <div>
                      <p>{connection.id}</p>
                    </div>
                  </StyledCol>
                </StyledTableRow>
                <StyledTableRow col="2" className="payment-details-content__row">
                  <StyledCol data-title={t("key")}>
                    {t("service")}
                  </StyledCol>
                  <StyledCol data-title={t("key")} className="service">
                    <div>
                      <p>{connection.service.name}</p>
                    </div>
                    <StyledConnectionLogo
                      icon={connection.service.name}
                      className="service__icon"
                    />
                  </StyledCol>
                </StyledTableRow>
                <StyledTableRow col="2" className="payment-details-content__row">
                  <StyledCol data-title={t("key")}>
                    {t("serviceId")}
                  </StyledCol>
                  <StyledCol data-title={t("value")}>
                    <p>{connection.service.id}</p>
                  </StyledCol>
                </StyledTableRow>
                <StyledTableRow col="2" className="payment-details-content__row">
                  <StyledCol data-title={t("key")}>
                    {t("project")}
                  </StyledCol>
                  <StyledCol data-title={t("value")}>
                    <p>{connection.project.name}</p>
                  </StyledCol>
                </StyledTableRow>
                <StyledTableRow col="2" className="payment-details-content__row">
                  <StyledCol data-title={t("key")}>
                    {t("projectId")}
                  </StyledCol>
                  <StyledCol data-title={t("value")}>
                    <p>{connection.project.id}</p>
                  </StyledCol>
                </StyledTableRow>
              </StyledTableBody>
            </StyledTable>
          </StyledFormBlock>

          <StyledFormBlock className="payment-details-content__block">
            <StyledBlockLabel>
              Flow data
            </StyledBlockLabel>
            {parsedFlowData.length && parsedFlowData.length !== 0 ?
              <StyledTable size="14">
                <StyledTableHead col="2" className="payment-details-content__head">
                  <StyledCol>{t("key")}:</StyledCol>
                  <StyledCol>{t("value")}:</StyledCol>
                </StyledTableHead>
                <StyledTableBody>
                  {parsedFlowData.map((dataItem, key) => (
                    <StyledTableRow
                      col="2"
                      key={key}
                      className={`payment-details-content__row ${dataItem.name === "errors" && "errors"}`}
                    >
                      <StyledCol data-title={t("key")}>
                        {dataItem.name}
                      </StyledCol>
                      <StyledCol data-title={t("value")}>
                        <p>
                          {transformExponentialNumber(dataItem.value, dataItem.name)}
                        </p>
                      </StyledCol>
                    </StyledTableRow>
                  ))}
                </StyledTableBody>
              </StyledTable>
              : <Alert severity="info">{t("infoMissing")}</Alert>}
          </StyledFormBlock>

          <StyledFormBlock className="payment-details-content__block">
            <StyledBlockLabel>
              Attributes
            </StyledBlockLabel>
            {attributes.length !== 0 ?
              <StyledTable size="14">
                <StyledTableHead col="2" className="payment-details-content__head">
                  <StyledCol>{t("key")}:</StyledCol>
                  <StyledCol>{t("value")}:</StyledCol>
                </StyledTableHead>
                <StyledTableBody>
                  {attributes.map((dataItem, key) => (
                    <StyledTableRow
                      col="2"
                      key={key}
                      className={`payment-details-content__row`}
                    >
                      <StyledCol data-title={t("key")}>
                        {dataItem.attribute.name}
                      </StyledCol>
                      <StyledCol data-title={t("value")}>
                        <p>
                          {dataItem.value}
                        </p>
                      </StyledCol>
                    </StyledTableRow>
                  ))}
                </StyledTableBody>
              </StyledTable>
              : <Alert severity="info">{t("attributesMissing")}</Alert>}
          </StyledFormBlock>
        </StyledFormGrid>
      </StyledPaymentDetailsContent>
    </>
  );
};

export default PaymentDetailsContent;